import axios from 'axios'
import { ElLoading, ElMessage } from 'element-plus'
import { isLogin } from '@/utils/auth'
// import qs from 'qs'

// 请求重试配置
const MAX_RETRY_COUNT = 3 // 最大重试次数
const RETRY_DELAY = 1000 // 重试延迟时间（毫秒）

let loadingService: any
let reqNum = 0

// 递归移除对象中值为 null 的字段
function removeNullFields(obj: any) {
  if (obj && typeof obj === 'object') {
    Object.keys(obj).forEach((key) => {
      if (obj[key] === null) {
        delete obj[key]
      }
      else if (typeof obj[key] === 'object') {
        removeNullFields(obj[key]) // 递归处理嵌套对象
      }
    })
  }
  return obj
}

// 扩展 AxiosRequestConfig 类型
declare module 'axios' {
  export interface AxiosInstance {
    /** 流式请求方法 */
    stream: any
    /** 基础 URL */
    baseUrl: string
    /** 上传文件方法 */
    uploadFile: any
  }
  export interface AxiosRequestConfig {
    retry?: boolean
    retryCount?: number
    /**
     * 是否根据角色请求
     * @default false
     */
    isForRole?: boolean
    /**
     * 是否虚拟实验，虚拟实验走vue2页面调其他系统的接口，单独处理
     * @default false
     */
    isSynthesis?: boolean
    isSynthesisLoading?: boolean
  }
}

const baseURL = (import.meta.env.DEV && import.meta.env.VITE_OPEN_PROXY) ? '/proxy' : import.meta.env.VITE_APP_API_BASEURL

const api = axios.create({
  baseURL,
  timeout: 1000 * 6000,
  responseType: 'json',
})

api.baseUrl = baseURL

api.interceptors.request.use(
  (config) => {
    const userStore = useUserStore()
    /**
     * 全局拦截请求发送前提交的参数
     * 以下代码为示例，在请求头里带上 token 信息
     */
    if (isLogin() && config.headers && !config.isSynthesis) {
      config.headers['x-token'] = userStore.token
    }
    if (config.isSynthesis && config.isSynthesisLoading) {
      startLoading()
    }
    if (config.isForRole) {
      config.url = `${userStore.userInfo.role}${config.url}`
    }
    // 是否将 POST 请求参数进行字符串化处理
    if (config.method === 'post') {
      // config.data = qs.stringify(config.data, {
      //   arrayFormat: 'brackets',
      // })
    }
    if (config.params) {
      config.params = removeNullFields(config.params)
    }

    // 处理请求体 (POST/PUT 等)
    if (config.data) {
      config.data = removeNullFields(config.data)
    }

    return config
  },
)

// 处理错误信息的函数
function handleError(error: any) {
  if (error.status === 401) {
    useUserStore().logout()
    throw error
  }
  let message = error.message
  if (message === 'Network Error') {
    message = '后端网络故障'
  }
  else if (message.includes('timeout')) {
    message = '接口请求超时'
  }
  else if (message.includes('Request failed with status code')) {
    message = `接口${message.substr(message.length - 3)}异常`
  }
  return Promise.reject(error)
}

api.interceptors.response.use(
  (response) => {
    if (response.config.responseType === 'blob') {
      return response
    }
    if (response.config.isSynthesis) {
      endLoading()
      if (response.data.code !== 200) {
        ElMessage.error(response.data.message || '请求失败')
        return Promise.reject(response)
      }
      return response.data
    }
    /**
     * 全局拦截请求发送后返回的数据，如果数据有报错则在这做全局的错误提示
     * 假设返回数据格式为：{ status: 1, error: '', data: '' }
     * 规则是当 status 为 1 时表示请求成功，为 0 时表示接口需要登录或者登录状态失效，需要重新登录
     * 请求出错时 error 会返回错误信息
     */
    if (response.data.status !== 1) {
      ElMessage.error(response.data.message || '请求失败')
      // useUserStore().logout()
      return Promise.reject(response)
    }
    return Promise.resolve(response.data)
  },
  async (error) => {
    endLoading()
    // 获取请求配置
    const config = error.config

    // 如果配置不存在或未启用重试，则直接处理错误
    if (!config || !config.retry) {
      return handleError(error)
    }

    // 设置重试次数
    config.retryCount = config.retryCount || 0

    // 判断是否超过重试次数
    if (config.retryCount >= MAX_RETRY_COUNT) {
      return handleError(error)
    }

    // 重试次数自增
    config.retryCount += 1

    // 延迟重试
    await new Promise(resolve => setTimeout(resolve, RETRY_DELAY))

    // 重新发起请求
    return api(config)
  },
)
api.uploadFile = (url: string, formData: any, config: any = { headers: { 'Content-Type': 'multipart/form-data' } }) => {
  return api.post(url, formData, config)
}
/** 流式请求方法 */
api.stream = (url: any, data?: any, streamData?: any, config?: any) => {
  const fullUrl = `${baseURL}${url}`
  return fetch(fullUrl, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: { 'Content-Type': 'application/json', 'x-token': useUserStore().token },
    ...config,
  })
    .then(async (response) => {
      // 检查响应状态
      if (!response.ok) {
        const errorText = await response.text()
        console.error(`请求失败 (${response.status}):`, errorText.substring(0, 500))
        ElMessage.error(`请求失败: ${response.status} ${response.statusText}`)
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const reader = response.body?.getReader()
      const decoder = new TextDecoder('utf-8')
      if (!reader) {
        throw new Error('无法读取响应体')
      }
      let buffer = '' // 累积缓冲区
      while (true) {
        const { value, done } = await reader.read()

        if (done) {
          // 处理最后剩余的buffer
          if (buffer.trim()) {
            const lines = buffer.split('\n')
            for (const line of lines) {
              const trimmedLine = line.trim()
              if (!trimmedLine) {
                continue
              }

              if (trimmedLine.startsWith('data:')) {
                const jsonStr = trimmedLine.substring(5).trim()
                if (jsonStr) {
                  try {
                    const parsed = JSON.parse(jsonStr)
                    streamData.value = parsed
                  }
                  catch (e: any) {
                    console.error('❌ 解析剩余SSE数据失败:', e.message)
                  }
                }
              }
              else if (trimmedLine.startsWith('{')) {
                try {
                  const parsed = JSON.parse(trimmedLine)
                  streamData.value = parsed
                }
                catch (e: any) {
                  console.error('❌ 解析剩余JSON数据失败:', e.message)
                }
              }
            }
          }
          break
        }

        // 将新的数据块添加到缓冲区
        const chunk = decoder.decode(value, { stream: true })

        // 检测到HTML响应（服务器错误）
        if (chunk.trim().startsWith('<!DOCTYPE') || chunk.trim().startsWith('<html')) {
          console.error('❌ 服务器返回了HTML错误页面而不是JSON数据')
          ElMessage.error('服务器处理请求时出错，请检查后端日志')
          throw new Error('服务器返回HTML错误页面')
        }

        buffer += chunk

        // 按行分割处理（SSE格式或纯JSON格式）
        const lines = buffer.split('\n')
        // 保留最后一个可能不完整的行
        buffer = lines.pop() || ''

        // 处理每一行
        for (const line of lines) {
          const trimmedLine = line.trim()
          if (!trimmedLine) {
            continue // 跳过空行
          }

          // SSE格式：data: {...}
          if (trimmedLine.startsWith('data:')) {
            const jsonStr = trimmedLine.substring(5).trim() // 移除 "data:" 前缀
            if (jsonStr) {
              try {
                const parsed = JSON.parse(jsonStr)
                streamData.value = parsed
              }
              catch (e: any) {
                console.error('❌ 解析SSE数据失败:', jsonStr.substring(0, 100), e.message)
              }
            }
          }
          // 也支持纯JSON格式（没有data:前缀）
          else if (trimmedLine.startsWith('{')) {
            try {
              const parsed = JSON.parse(trimmedLine)
              streamData.value = parsed
            }
            catch (e: any) {
              console.error('❌ 解析JSON数据失败:', trimmedLine.substring(0, 100), e.message)
            }
          }
        }
      }
    })
    .catch((error) => {
      // 如果是 AbortError，不打印错误（这是正常的中止操作）
      if (error.name !== 'AbortError') {
        console.error('❌ 请求失败:', error)
      }
    })
}

function startLoading() {
  if (reqNum === 0) {
    loadingService = ElLoading.service({
      lock: true,
      fullscreen: true,
      text: '正在计算中',
      background: 'rgba(255,255,255,0)',
    })
  }
  reqNum++
}

// 延迟 300ms 再调用 closeLoading 方法，合并300ms内的请求
function endLoading() {
  setTimeout(closeLoading, 300)
}

/* 结束动画 */
function closeLoading() {
  if (reqNum <= 0) {
    return
  }
  reqNum--
  if (reqNum === 0) {
    loadingService.close()
  }
}

export default api
